import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MessageSquare, AtSign, Bell, Check, Trash2 } from 'lucide-react'
import { useNavigate } from './nav'
import type { RootState } from '../store'
import type { AppNotification } from '../data/projectTypes'
import {
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useClearAllNotificationsMutation,
} from '../services/projectDataApi'
import './NotificationCenter.css'

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sec = Math.round((Date.now() - d.getTime()) / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  return d.toLocaleDateString()
}

// The deep link to the artifact a notification is about, anchored to the comment thread so the target
// page scrolls straight to it (see CommentsSection's #comments handling).
function notificationHref(n: AppNotification): string {
  const seg = n.target_type === 'test_run' ? 'test-runs' : 'bugs'
  return `/projects/${n.project_id}/${seg}/${n.target_id}#comments`
}

// The in-app Notification Center: a bell trigger with an unread badge that opens a dropdown of
// the current user's comment/mention notifications. Clicking one deep-links to the Bug or Test Run and
// marks it read. Renders nothing when signed out.
export default function NotificationCenter({ variant = 'sidebar' }: { variant?: 'sidebar' | 'topbar' }) {
  const user = useSelector((state: RootState) => state.auth.user)
  const [open, setOpen] = useState(false)
  // Two-step intent check for the destructive "Clear all": the first click arms the button, a second
  // click commits. Nothing is deleted on the first click, so an accidental tap can't wipe the feed.
  const [confirmingClear, setConfirmingClear] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const notificationsQuery = useGetNotificationsQuery(undefined, {
    skip: !user,
    pollingInterval: 30_000,
    skipPollingIfUnfocused: true,
  })
  const [markRead] = useMarkNotificationsReadMutation()
  const [clearAll] = useClearAllNotificationsMutation()

  const items = notificationsQuery.data?.items ?? []
  const unread = notificationsQuery.data?.unread ?? 0

  // Reset the arm state whenever the panel closes, so reopening never starts mid-confirmation.
  useEffect(() => {
    if (!open) setConfirmingClear(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (!user) return null

  const onOpenNotification = (n: AppNotification) => {
    setOpen(false)
    if (!n.read_at) void markRead({ ids: [n.id] })
    navigate(notificationHref(n))
  }

  const onMarkAll = () => {
    if (unread > 0) void markRead({ all: true })
  }

  const onClearAll = () => {
    if (items.length === 0) return
    if (!confirmingClear) {
      setConfirmingClear(true)
      return
    }
    setConfirmingClear(false)
    void clearAll()
  }

  return (
    <div ref={containerRef} className={`notif notif--${variant}`}>
      <button
        type="button"
        className="notif__trigger"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-center-trigger"
        data-analytics-event="notification-center-toggle"
      >
        <Bell size={18} strokeWidth={1.75} aria-hidden />
        {unread > 0 && (
          <span className="notif__badge" data-testid="notification-badge" aria-hidden>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif__panel" role="menu" data-testid="notification-panel">
          <div className="notif__head">
            <span className="notif__title">Notifications</span>
            {items.length > 0 && (
              <div className="notif__actions">
                {unread > 0 && (
                  <button
                    type="button"
                    className="notif__action notif__mark-all"
                    onClick={onMarkAll}
                    data-testid="notification-mark-all"
                    data-analytics-event="notification-mark-all"
                  >
                    <Check size={13} aria-hidden /> Mark all read
                  </button>
                )}
                <button
                  type="button"
                  className={`notif__action notif__clear-all${confirmingClear ? ' notif__clear-all--confirm' : ''}`}
                  onClick={onClearAll}
                  data-testid="notification-clear-all"
                  data-analytics-event="notification-clear-all"
                >
                  <Trash2 size={13} aria-hidden /> {confirmingClear ? 'Confirm clear?' : 'Clear all'}
                </button>
              </div>
            )}
          </div>
          <div className="notif__list">
            {notificationsQuery.isLoading ? (
              // Opening the bell before the first fetch returns would otherwise claim the user is all
              // caught up and then fill in with notifications.
              <p className="notif__empty" role="status">Loading notifications…</p>
            ) : items.length === 0 ? (
              <p className="notif__empty">You’re all caught up.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  role="menuitem"
                  className={`notif__item ${n.read_at ? '' : 'notif__item--unread'}`}
                  onClick={() => onOpenNotification(n)}
                  data-testid="notification-item"
                  data-analytics-event="notification-open"
                >
                  <span className="notif__item-icon" aria-hidden>
                    {n.type === 'mention' ? <AtSign size={15} /> : <MessageSquare size={15} />}
                  </span>
                  <span className="notif__item-body">
                    <span className="notif__item-text">
                      <strong>{n.actor_name || 'Someone'}</strong>
                      {n.type === 'mention'
                        ? ' mentioned you in a comment on '
                        : ' commented on '}
                      {n.target_type === 'test_run' ? 'a test run' : 'a bug'}
                    </span>
                    {n.content && <span className="notif__item-snippet">{n.content}</span>}
                    <span className="notif__item-when">{formatWhen(n.created_at)}</span>
                  </span>
                  {!n.read_at && <span className="notif__item-dot" aria-hidden />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
